import React from "react";
import { Eye, Zap, Award, Globe, Target, Check } from 'lucide-react';

const VisionAndMissionPage = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
          {/* Section Header */}
          <div className="text-left mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-blue-100 to-orange-100 text-blue-800 text-xs font-medium mb-3">
              MISSION & VISION
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Driving <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-orange-500">Innovation</span> Forward
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-orange-500 rounded-full"></div>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Mission Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl transform group-hover:scale-105 transition-all duration-500"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-500">
                {/* Card Header */}
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">MISSION</h3>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
                  </div>
                </div>

                {/* Mission Content */}
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed text-sm">
                    To redefine industry standards by delivering premium-quality products that combine innovation, affordability, and sustainability.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Strategic Alliances</h4>
                        <p className="text-gray-600 text-xs">Building powerful partnerships for mutual growth</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Unwavering Commitment</h4>
                        <p className="text-gray-600 text-xs">Excellence, customer satisfaction, and operational efficiency</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Unparalleled Value</h4>
                        <p className="text-gray-600 text-xs">Creating exceptional value for partners and end consumers</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vision Card */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl transform group-hover:scale-105 transition-all duration-500"></div>
              <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-500">
                {/* Card Header */}
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">VISION</h3>
                    <div className="w-10 h-0.5 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
                  </div>
                </div>

                {/* Vision Content */}
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed text-sm">
                    To be a global leader in multi-sector trade, pioneering groundbreaking advancements in aviation, FMCG, personal care, and fashion.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Zap className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Empower Businesses</h4>
                        <p className="text-gray-600 text-xs">Transforming industries and enriching lives</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Award className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Set New Benchmarks</h4>
                        <p className="text-gray-600 text-xs">Quality, trust, and innovation across every market</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-6 h-6 bg-orange-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Globe className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Global Leadership</h4>
                        <p className="text-gray-600 text-xs">Multi-sector trade excellence worldwide</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-md border border-gray-100">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-gray-700 font-medium text-sm">Committed to Excellence Since 2018</span>
            </div>
          </div>
        </div>
      </section>
  );
};

export default VisionAndMissionPage; 